import { useMemo } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { CATEGORIES, CATEGORY_MAP } from "../utils/constants";
import { getMonthKey, getMonthLabel, getRelativeDate } from "../utils/helpers";
import ProfileAvatar from "./ProfileAvatar";
import styles from "../styles/theme";

export default function DashboardView({
  people,
  onViewChange,
  onSelectPerson,
}) {
  const stats = useMemo(() => {
    const total = people.length;
    const thisMonth = people.filter(
      (p) => getMonthKey(p.dateAdded) === getMonthKey(new Date()),
    ).length;
    const categories = {};
    CATEGORIES.forEach((c) => (categories[c.id] = 0));
    people.forEach((p) => categories[p.category]++);

    // Monthly data (last 6 months)
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const key = getMonthKey(d);
      months.push({ key, label: getMonthLabel(key), count: 0 });
    }
    people.forEach((p) => {
      const mk = getMonthKey(p.dateAdded);
      const m = months.find((mm) => mm.key === mk);
      if (m) m.count++;
    });

    // Cumulative
    let cum = total - months.reduce((s, m) => s + m.count, 0);
    months.forEach((m) => {
      cum += m.count;
      m.cumulative = cum;
    });

    const pieData = CATEGORIES.map((c) => ({
      name: c.label,
      value: categories[c.id],
      color: c.color,
      icon: c.icon,
    })).filter((d) => d.value > 0);

    // Recent
    const recent = [...people]
      .sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded))
      .slice(0, 5);

    return { total, thisMonth, categories, months, pieData, recent };
  }, [people]);

  if (people.length === 0) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyIcon}>🌱</div>
        <h2 style={styles.emptyTitle}>Start Your People Journal</h2>
        <p style={styles.emptyText}>
          Add the first person you&apos;d like to remember. Build your network,
          one connection at a time.
        </p>
        <div style={styles.emptyHints}>
          {CATEGORIES.slice(0, 4).map((c) => (
            <span
              key={c.id}
              style={{ ...styles.emptyHint, borderColor: c.color }}
            >
              {c.icon} {c.label}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.dashboard}>
      {/* Stat Cards */}
      <div style={styles.statCards}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.total}</div>
          <div style={styles.statLabel}>Total People</div>
        </div>
        <div style={{ ...styles.statCard, borderLeftColor: "#4A7C6F" }}>
          <div style={styles.statValue}>{stats.thisMonth}</div>
          <div style={styles.statLabel}>Added This Month</div>
        </div>
        <div style={{ ...styles.statCard, borderLeftColor: "#7B68AE" }}>
          <div style={styles.statValue}>
            {CATEGORIES.filter((c) => stats.categories[c.id] > 0).length}
          </div>
          <div style={styles.statLabel}>Active Categories</div>
        </div>
        <div style={{ ...styles.statCard, borderLeftColor: "#C2785C" }}>
          <div style={styles.statValue}>
            {stats.months[stats.months.length - 1]?.count || 0}
          </div>
          <div style={styles.statLabel}>New This Month</div>
        </div>
      </div>

      {/* Charts Row */}
      <div style={styles.chartsRow}>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>People Added Over Time</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={stats.months}
              margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2e" />
              <XAxis dataKey="label" tick={{ fill: "#9a9a9e", fontSize: 11 }} />
              <YAxis
                tick={{ fill: "#9a9a9e", fontSize: 11 }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: "#1e1e22",
                  border: "1px solid #333",
                  borderRadius: 8,
                  color: "#e8e4df",
                }}
              />
              <Bar
                dataKey="count"
                fill="#4A7C6F"
                radius={[4, 4, 0, 0]}
                name="People Added"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Network Growth</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart
              data={stats.months}
              margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2e" />
              <XAxis dataKey="label" tick={{ fill: "#9a9a9e", fontSize: 11 }} />
              <YAxis
                tick={{ fill: "#9a9a9e", fontSize: 11 }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: "#1e1e22",
                  border: "1px solid #333",
                  borderRadius: 8,
                  color: "#e8e4df",
                }}
              />
              <Line
                type="monotone"
                dataKey="cumulative"
                stroke="#C2785C"
                strokeWidth={2.5}
                dot={{ r: 4, fill: "#C2785C" }}
                name="Total People"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Row */}
      <div style={styles.chartsRow}>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>By Category</h3>
          {stats.pieData.length > 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie
                    data={stats.pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={80}
                    dataKey="value"
                    stroke="none"
                  >
                    {stats.pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "#1e1e22",
                      border: "1px solid #333",
                      borderRadius: 8,
                      color: "#e8e4df",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={styles.pieLegend}>
                {stats.pieData.map((d) => (
                  <div key={d.name} style={styles.pieLegendItem}>
                    <span
                      style={{ ...styles.pieLegendDot, background: d.color }}
                    />
                    <span>
                      {d.icon} {d.name}
                    </span>
                    <span style={styles.pieLegendCount}>{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p style={{ color: "#777" }}>No data yet</p>
          )}
        </div>

        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Recently Added</h3>
          <div style={styles.recentList}>
            {stats.recent.map((p) => (
              <div
                key={p.id}
                style={styles.recentItem}
                onClick={() => onSelectPerson(p)}
              >
                <ProfileAvatar
                  person={p}
                  size={36}
                  fontSize={14}
                  color={CATEGORY_MAP[p.category]?.color}
                />
                <div style={styles.recentInfo}>
                  <div style={styles.recentName}>{p.name}</div>
                  <div style={styles.recentMeta}>
                    {CATEGORY_MAP[p.category]?.icon} {p.relationship} ·{" "}
                    {getRelativeDate(p.dateAdded)}
                  </div>
                </div>
                <span style={styles.recentArrow}>→</span>
              </div>
            ))}
          </div>
          {people.length > 5 && (
            <button
              onClick={() => onViewChange("people")}
              style={styles.viewAllBtn}
            >
              View all people →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
